import { STORY_SOURCE, story } from '@/content/storyBuilder'

// The seventh written page. Everything below comes from the three-part
// full-page capture of digitopia.co/customers/a-101/ sent on 2026-08-28 —
// "birebir tıpa tıp aynısını tasarla" — with the two photographs on it: the
// mother and child at the produce shelves in the header, and the couple in
// the aisle across the full width under the standfirst. The closing quote is
// not a `section`: Orhan Dağlıoğlugil's words have been in
// `content/testimonials.ts` keyed to this slug since the Hall of Fame
// shipped, A101's mark and his portrait included, and `StoryBody` draws that
// card — the testimonial template the same request asked for at the foot of
// the page. Reading the quote off this capture is what confirmed the
// transcription: it came back word for word identical to the catalogue's.
//
// The one thing this page needed that the six before it did not is a section
// with **no heading** — its story is two paragraphs of continuous prose after
// the picture, not a set of headed parts. See `StorySection.heading`.
export const added = story('a-101', 'A 101', {
  doc: 'e11ae84a-4638-4968-b28b-003d640afe83-A-101 and Digitopia.pdf',
  lede: ['The retail industry goes through tectonic shifts…'],
  heroPhoto: {
    src: '/photo/a101-mother-and-child-in-store.webp',
    alt: 'A mother holding her small daughter at a supermarket’s fresh produce shelves, the '
      + 'child reaching out for a lime.',
  },
  intro:
    '“A101 grows at an incredible pace. They opened 1.000 new stores every year, for the last '
    + 'few years, now having more than 10.000 stores. At the same time, they invest hugely in '
    + 'digital channels, and the volume there is growing as much, almost doubling every year, '
    + 'for the last few years. During the pandemic they introduced a brand-new '
    + 'last-mile-delivery service, being loved, and rapidly adopted by consumers.”',
  photo: {
    src: '/photo/a101-couple-in-produce-aisle.webp',
    alt: 'A couple with a shopping trolley reading the label on a punnet of strawberries, '
      + 'beside a long run of green crates of fruit and vegetables.',
  },
  sections: [
    {
      body: [
        'So, in general A101 is getting more and more digital, accumulating much more data, '
        + 'running much better optimised processes. But where are they on their overall digital '
        + 'journey? How do they compare against their peers? As they get answers to these '
        + 'questions, they will be able to better manage and govern their digital '
        + 'transformation journey. Digitopia has been appointed to measure and benchmark '
        + 'A101’s digital maturity. In June 2020 the first has been conducted. Since then, '
        + 'Digitopia is working with A101’s upper management on their digital transformation '
        + 'and has become a truly trusted advisor. Digitopia invited all relevant stakeholders '
        + 'and top management to six sessions covering the dimensions of the “Digital Maturity '
        + 'Index.” A101 participants benefited very much of discussing jointly their current '
        + 'state and future targets in a very structured manner. The report provided the '
        + 'digital maturity score for the first time. A101 also realised where they are '
        + 'compared against national and international retailers. The recommendations were '
        + 'prioritised jointly and compiled into a holistic roadmap. Digitopia has been '
        + 'appointed as the consultancy to continue the top-level portfolio and program '
        + 'management for A101.',
        'Knowing is such a strong condition, compared to not knowing. A101 now knows exactly '
        + 'and very precisely where they are on their digital transformation journey, on six '
        + 'different dimensions, by more than 120 different topics. Now they can very '
        + 'specifically define their strategies, their targets, and prioritise their '
        + 'investments. By repeating the digital maturity assessment, they will see their '
        + 'progress, how they keep pace with competition, and how they benefit from all the '
        + 'investments in digital transformation. Upper management is monitoring the digital '
        + 'transformation now very closely and has a powerful measurement tool to decide on '
        + 'priorities and investments, and understand the impact and benefits. Another huge '
        + 'benefit is a common understanding of what digital transformation truly is, why it '
        + 'is important, and agree on a shared vision and common objectives. This is what '
        + 'Digitopia usually calls alignment. And A101 benefited greatly from this alignment.',
      ],
    },
  ],
  check:
    'This page’s words were transcribed from a three-part full-page capture of '
    + `${STORY_SOURCE}/a-101/ supplied on 2026-08-28, not from a file we hold, so please `
    + 'proofread it against the source before production — particularly the figures in the '
    + 'opening quotation (1.000 new stores a year, more than 10.000 stores, both written with '
    + 'the European thousands separator the reference uses) and in the closing paragraph (six '
    + 'dimensions, more than 120 topics, the June 2020 first measurement). The reference’s '
    + 'British spellings — “optimised”, “prioritised”, “realised” — are kept verbatim rather '
    + 'than Americanised, and its own inconsistency between “A 101” in the headline and “A101” '
    + 'in the prose is kept too, because the headline is this catalogue’s company name and the '
    + 'prose is the customer’s. Three deliberate differences from the reference, each of them '
    + 'a template-wide decision rather than this page’s: the header eyebrow reads “Digitopia / '
    + 'Customer Success Story” where digitopia.co reads “SUCCESS STORIES”; the opening '
    + 'quotation sits in the grey standfirst band every written story uses, where the '
    + 'reference sets it as centred text on the page; and the reference’s “Download” button '
    + 'under the story is here the one “Read the full story (PDF)” button in the header, '
    + 'pointing at the same document. One gap: that quotation is unattributed on the reference '
    + 'as well as here — it reads as Digitopia’s own account of the account, and if it belongs '
    + 'to a named person it should say so. Lastly, the two photographs are 768px and 1200px '
    + 'originals, which is what the live site serves, so they are drawn at that width and will '
    + 'look soft on a high-density screen until larger files arrive.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 9
